import { Avatar, Button, Flex } from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { MixpanelClient } from '~/plugins/mixpanel/client'
import { AgentChatInput } from '../AgentAssistant/components/AgentChatInput'
import { useToolbarPosition } from '../AgentAssistant/hooks/useToolbarPosition'

type Props = {
  agent?: OrganizationMemberFromContext
  imageSrc: string
  rect: DOMRect
  containerRef: React.RefObject<HTMLDivElement>
  isUpdating: boolean
  onEdit?: (imageSrc: string, prompt: string) => void
  className: string
}

export const ImageAssistant: React.FC<Props> = ({
  agent,
  imageSrc,
  rect,
  containerRef,
  isUpdating,
  onEdit,
  className,
}) => {
  const mixpanel = MixpanelClient.use()
  const containerRect = containerRef.current?.getBoundingClientRect()
  const inputRef = useRef<HTMLDivElement>(null)
  const toolbarStyle = useToolbarPosition(containerRef)

  const [isAgentInputVisible, setIsAgentInputVisible] = useState(false)

  useEffect(() => {
    if (!isUpdating) {
      setIsAgentInputVisible(false)
    }
  }, [agent, isUpdating])

  if (!containerRect) {
    return null
  }

  const left = rect.left - containerRect.left
  const top = rect.top - containerRect.top

  const handleSubmit = (prompt: string) => {
    onEdit(imageSrc, prompt)
  }

  return (
    <div className={className}>
      {!isAgentInputVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            top: top - 60,
            left: left,
            pointerEvents: 'auto',
          }}
        >
          {' '}
          <Flex gap={8} flex={1} align="center">
            <Avatar
              size={54}
              src={agent?.appSession.pictureUrl}
              style={{ flexShrink: 0, border: '1px solid grey' }}
            />
            <Button
              type="primary"
              onClick={() => {
                mixpanel.track('blogWriter.image.assistant.button.clicked')
                setIsAgentInputVisible(true)
              }}
            >
              Update image
            </Button>
          </Flex>
        </motion.div>
      )}

      <AnimatePresence>
        {isAgentInputVisible && agent && (
          <motion.div
            ref={inputRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ ...toolbarStyle, zIndex: 1 }}
            onAnimationComplete={() => {
              const senderEl = inputRef.current?.querySelector('textarea')
              senderEl?.focus()
            }}
          >
            <AgentChatInput
              agent={agent}
              onSubmit={handleSubmit}
              isUpdating={isUpdating}
              selectionType="image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
